import { Link } from "react-router-dom";
import { Check, Gift, AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Reveal } from "./Section";
import { usePlans, useOffer, formatGBP } from "@/lib/api";
import { cn } from "@/lib/utils";

export const PlanCard = ({ plan, offer, index = 0 }) => {
  const popular = plan.popular;
  return (
    <Reveal delay={index * 0.08} className="h-full">
      <article
        data-testid={`plan-card-${plan.slug}`}
        className={cn(
          "relative flex h-full flex-col rounded-3xl border p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1",
          popular
            ? "border-[#D4A437]/60 bg-[#15110d] shadow-[0_40px_90px_-40px_rgba(255,106,0,0.45)]"
            : "border-[#2E2E2E] bg-[#111111] hover:border-[#444]",
        )}
      >
        {popular && (
          <span className="absolute -top-3.5 left-7 whitespace-nowrap rounded-full bg-[#D4A437] px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#080808]" data-testid="plan-popular-badge">
            Most Popular
          </span>
        )}
        <h3 className="font-display text-2xl font-bold">{plan.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA] min-h-[2.75rem]">{plan.description}</p>
        <div className="mt-7 flex items-end gap-1.5">
          <span className="font-display text-5xl font-extrabold tracking-tight" data-testid={`plan-price-${plan.slug}`}>
            {formatGBP(plan.price_pence)}
          </span>
          <span className="pb-1.5 text-sm text-[#A1A1AA]">/month</span>
        </div>
        <div className="my-7 h-px bg-[#262626]" />
        <ul className="space-y-3.5 text-[15px]">
          {plan.contents.map((c) => (
            <li key={c} className="flex gap-3">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#FF6A00]" aria-hidden="true" />
              <span className="text-[#F7F5F0]/90">{c}</span>
            </li>
          ))}
        </ul>
        {offer?.active && (
          <div className="mt-6 flex gap-3 rounded-xl border border-[#D4A437]/30 bg-[#D4A437]/10 p-3.5 text-sm text-[#F3E0A2]" data-testid={`plan-offer-${plan.slug}`}>
            <Gift className="h-5 w-5 shrink-0" /> {offer.name}: {offer.description}
          </div>
        )}
        <div className="mt-auto pt-8">
          <Link to={`/checkout?plan=${plan.slug}`} className={cn("w-full", popular ? "lc-btn-primary" : "lc-btn-ghost")} data-testid={`plan-select-${plan.slug}`}>
            Choose {plan.name}
          </Link>
          <p className="mt-3 text-center text-xs text-[#7a7a7a]">Monthly rolling · Pause, skip or cancel online</p>
        </div>
      </article>
    </Reveal>
  );
};

export const PlansGrid = () => {
  const { data: plans, isLoading, isError, refetch } = usePlans();
  const { data: offer } = useOffer();

  if (isLoading)
    return (
      <div className="grid gap-6 md:grid-cols-3" data-testid="plans-loading">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[520px] rounded-3xl bg-[#151515]" />)}
      </div>
    );
  if (isError)
    return (
      <div className="lc-card flex flex-col items-center gap-4 p-10 text-center" data-testid="plans-error">
        <AlertCircle className="h-8 w-8 text-[#FF6A00]" />
        <p className="text-[#A1A1AA]">We couldn't load our plans just now.</p>
        <button onClick={() => refetch()} className="lc-btn-ghost">Try again</button>
      </div>
    );
  return (
    <div className="grid gap-6 pt-4 md:grid-cols-3 lg:gap-8" data-testid="plans-grid">
      {plans.map((p, i) => <PlanCard key={p.slug} plan={p} offer={offer} index={i} />)}
    </div>
  );
};
